<!-- UI-5 (5f): per-task configure + run popup for the Pipelines flyout. Holds the run
     orchestration relocated from AppBottomDrawer (create → provision → run), so the bottom
     drawer is no longer needed. Emits `ran` (a new TaskRun) so the flyout refreshes its
     recent-runs immediately, and `close`. -->
<template>
  <div class="modal-card pipeline-run-modal">
    <header class="modal-card-head">
      <div class="prm-head-text">
        <p class="modal-card-title">{{ task.name }}</p>
        <span class="prm-version">{{ task.namespace }} · {{ task.version }}</span>
      </div>
      <button type="button" class="delete" @click="$emit('close')"></button>
    </header>

    <section class="modal-card-body">
      <p v-if="task.description" class="prm-desc">{{ task.description }}</p>

      <div class="prm-inputs">
        <task-input-form
          :inputs="inputs"
          :task="task"
          @input="inputs = $event"
        />
      </div>
    </section>

    <footer class="modal-card-foot">
      <b-button @click="$emit('close')">{{ $t('button-cancel') }}</b-button>
      <b-button
        type="is-primary"
        icon-left="play"
        :loading="isRunning"
        @click="run"
      >
        {{ $t('run') }}
      </b-button>
    </footer>
  </div>
</template>

<script>
import Task from '@/utils/appengine/task';
import TaskRun from '@/utils/appengine/task-run';
import TaskInputForm from '@/components/appengine/forms/TaskInputForm';

export default {
  name: 'pipeline-run-modal',
  components: {TaskInputForm},
  props: {
    task: {type: Object, required: true},
    projectId: {type: [String, Number], required: true},
    imageId: {type: [String, Number], required: true},
  },
  data() {
    return {
      inputs: {},
      isRunning: false,
    };
  },
  computed: {
    hasBinaryData() {
      return Object.values(this.inputs).some(p => p && (p.value instanceof File || p.value instanceof Blob));
    }
  },
  methods: {
    getInputProvisions() {
      return Object.entries(this.inputs).map(([parameterName, {type, value}]) => ({
        'param_name': parameterName,
        type,
        value,
      }));
    },
    async run() {
      this.isRunning = true;
      try {
        let taskRun = await Task.createTaskRun(
          this.projectId,
          this.task.namespace,
          this.task.version,
          this.imageId,
        );

        let provisions = this.getInputProvisions();
        if (this.hasBinaryData) {
          for (const provision of provisions) {
            let body = provision;
            if (provision.value instanceof File || provision.value instanceof Blob) {
              body = new FormData();
              body.append('file', provision.value, provision.value.name || 'uploaded-file');
            }
            await Task.singleProvisionTask(this.projectId, taskRun.id, provision.param_name, body);
          }
        } else {
          await Task.batchProvisionTask(this.projectId, taskRun.id, provisions);
        }

        let event = await Task.runTask(this.projectId, taskRun.id);
        let started = new TaskRun(event.resource);
        started.project = this.projectId;

        this.$buefy.toast.open({message: this.$t('app-engine.run.started'), type: 'is-success'});
        this.$emit('ran', started);
        this.$emit('close');
      } catch (e) {
        const serverError = e.response && e.response.data
          ? (e.response.data.message || e.response.data.errorCode)
          : e.message;
        this.$buefy.toast.open({message: `Error : ${serverError}`, type: 'is-danger', indefinite: true});
      }
      this.isRunning = false;
    }
  }
};
</script>

<style scoped>
.pipeline-run-modal {
  width: 440px;
  max-width: calc(100vw - 2rem);
}

.modal-card-head {
  align-items: flex-start;
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e8ee;
}

.prm-head-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.modal-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #0e1116;
}

.prm-version {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  color: #8a91a1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-card-body {
  padding: 16px;
  overflow-x: hidden;
}

.prm-desc {
  font-size: 13px;
  color: #5a6172;
  margin-bottom: 1.25rem;
  line-height: 1.5;
}

/* Contain + full-width the app-engine field components (built for the wide drawer). */
.prm-inputs ::v-deep .container {
  width: 100%;
}

.prm-inputs ::v-deep .field {
  width: 100%;
  margin-bottom: 0;
}

.prm-inputs ::v-deep .b-numberinput,
.prm-inputs ::v-deep .control {
  width: 100%;
}

.prm-inputs ::v-deep input {
  width: 100%;
  min-width: 0;
}

/* spacing between stacked fields */
.prm-inputs ::v-deep .container + .container {
  margin-top: 1.1rem;
}

.modal-card-foot {
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #fafbfc;
  border-top: 1px solid #e5e8ee;
}
</style>
